import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Package, LockKey, User, IdentificationBadge, CheckCircle } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setBusy(true);
    const r = await register(username.trim(), password, fullName.trim() || null);
    setBusy(false);
    if (r.ok) {
      setDone(true);
    } else {
      toast.error(r.error);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center fade-up">
          <div className="w-16 h-16 rounded-sm bg-[#FACC15]/15 border border-[#FACC15]/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} color="#FACC15" weight="duotone" />
          </div>
          <h1 className="font-display text-3xl mb-3">Cuenta creada</h1>
          <p className="text-white/60 text-sm mb-8">
            Tu solicitud fue enviada. Un administrador debe aprobar tu cuenta antes de que puedas
            ingresar.
          </p>
          <Link
            to="/login"
            data-testid="back-to-login"
            className="inline-block px-5 py-2.5 rounded-sm bg-[#0055FF] text-sm font-semibold hover:brightness-110 transition-transform active:scale-[0.98]"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-sm bg-[#0055FF] flex items-center justify-center">
            <Package size={22} weight="fill" color="#fff" />
          </div>
          <div>
            <div className="font-display text-xl leading-none">WAREHOUSE RCN</div>
            <div className="font-mono-data text-[10px] tracking-widest text-white/50 mt-1">
              CREAR · CUENTA
            </div>
          </div>
        </div>

        <h1 className="font-display text-3xl mb-2">Solicita acceso</h1>
        <p className="text-white/60 mb-6 text-sm">
          Un administrador revisará tu solicitud. No se requiere correo electrónico.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Field
            label="Nombre Completo"
            icon={IdentificationBadge}
            value={fullName}
            onChange={setFullName}
            testId="reg-fullname"
            placeholder="Juan Pérez"
          />
          <Field
            label="Usuario"
            icon={User}
            value={username}
            onChange={setUsername}
            testId="reg-username"
            placeholder="jperez"
            required
          />
          <Field
            label="Contraseña"
            icon={LockKey}
            value={password}
            onChange={setPassword}
            testId="reg-password"
            type="password"
            required
          />
          <Field
            label="Confirmar Contraseña"
            icon={LockKey}
            value={confirm}
            onChange={setConfirm}
            testId="reg-confirm"
            type="password"
            required
          />

          <button
            data-testid="register-submit-button"
            disabled={busy}
            className="w-full py-3 rounded-sm bg-[#0055FF] text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {busy ? "Enviando…" : "Solicitar acceso"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-white/50">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-[#0055FF] hover:brightness-125">
            Ingresa
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, value, onChange, testId, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
        {label}
      </label>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid={testId}
          type={type}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-3 py-3 rounded-sm bg-[#111111] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
        />
      </div>
    </div>
  );
}
